import React, { useState, useMemo, useCallback, useEffect } from 'react';
import Card from '../../components/card/Card';
import P from '../../components/texts/p/P';
import Button from '../../components/button/Button';
import Input from '../../components/input/Input';
import { downloadClientDocument, downloadLufeCloudFile } from '../../services/ClientService';
import { downloadSignerDocument, getFileTypes } from '../../services/ApplicationService';
import { getMimeType } from '../../utils/Utils';
import theme from '../../styles/theme';
import { H } from '../../components/texts/h/H.styles';
import JSZip from 'jszip';

interface FileType {
  id: number;
  code: string;
  description: string;
}

interface FileData {
  type: string;
  file_name: string;
  is_basic_data?: boolean;
  id_application?: number;
  updated_at?: string;
  is_application_file?: boolean;
}

interface DocumentationTabProps {
  files: FileData[];
  application_files?: FileData[];
  cuit: string;
  clientTheme?: boolean;
}

interface GroupedFiles {
  [type: string]: {
    title: string;
    files: FileData[];
  };
}

const DocumentationTab: React.FC<DocumentationTabProps> = ({ 
  files, 
  application_files = [],
  cuit, 
  clientTheme 
}) => {
  const [selectedFiles, setSelectedFiles] = useState<Set<string>>(new Set());
  const [downloadingFiles, setDownloadingFiles] = useState<Set<string>>(new Set());
  const [bulkDownloading, setBulkDownloading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [fileTypes, setFileTypes] = useState<FileType[]>([]);

  // Load file types from API
  useEffect(() => {
    const loadFileTypes = async () => {
      try {
        const response = await getFileTypes();
        if (response.success && 'data' in response && response.data) {
          setFileTypes(response.data);
        }
      } catch (error) {
        console.error('Error loading file types:', error);
      }
    };
    loadFileTypes();
  }, []);

  // Add CSS to disable hover on buttons
  React.useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      .documentation-tab-no-hover button:hover,
      .documentation-tab-no-hover button:focus {
        background-color: transparent !important;
        box-shadow: none !important;
        pointer-events: auto !important;
        cursor: pointer !important;
      }
      .documentation-tab-no-hover button:active {
        background-color: transparent !important;
        box-shadow: none !important;
      }
      .documentation-tab-no-hover button {
        pointer-events: auto !important;
        cursor: pointer !important;
      }
    `;
    document.head.appendChild(style);
    
    return () => {
      if (document.head.contains(style)) {
        document.head.removeChild(style);
      }
    };
  }, []);


  // Get document titles from API file types
  const getDocumentTitle = useCallback((type: string): string => {
    const fileType = fileTypes.find(ft => ft.code === type);
    if (fileType) {
      return fileType.description;
    }
    
    return `Documento ${type}`;
  }, [fileTypes]);

  // Group files by type and filter by search term
  const groupedFiles: GroupedFiles = useMemo(() => {
    const grouped: GroupedFiles = {};
    
    // Combine all files from both sources and mark application files
    const allFiles = [
      ...files.map(f => ({ ...f, is_application_file: false })),
      ...application_files.map(f => ({ ...f, is_application_file: true }))
    ];
    
    // Remove duplicates: keep only the file with the most recent updated_at
    const uniqueFilesMap = new Map<string, FileData>();
    
    allFiles.forEach(file => {
      const key = `${file.type}_${file.file_name}`;
      const existing = uniqueFilesMap.get(key);
      
      if (!existing) {
        uniqueFilesMap.set(key, file);
      } else {
        // Compare updated_at dates
        const existingDate = existing.updated_at ? new Date(existing.updated_at).getTime() : 0;
        const currentDate = file.updated_at ? new Date(file.updated_at).getTime() : 0;
        
        // Keep the one with the most recent date
        if (currentDate > existingDate) {
          uniqueFilesMap.set(key, file);
        }
      }
    });
    
    const uniqueFiles = Array.from(uniqueFilesMap.values());
    
    const filteredFiles = uniqueFiles.filter(file => {
      if (!searchTerm) return true;
      const fileName = file.file_name?.toLowerCase() || '';
      return fileName.includes(searchTerm.toLowerCase());
    });
    
    filteredFiles.forEach(file => {
      if (!grouped[file.type]) {
        grouped[file.type] = {
          title: getDocumentTitle(file.type),
          files: []
        };
      }
      grouped[file.type].files.push(file);
    });
    
    return grouped;
  }, [files, application_files, getDocumentTitle, searchTerm]);



  const handleFileSelection = (fileName: string) => {
    const newSelected = new Set(selectedFiles);
    if (newSelected.has(fileName)) {
      newSelected.delete(fileName);
    } else {
      newSelected.add(fileName);
    }
    setSelectedFiles(newSelected);
  };

  const handleViewFile = async (file: FileData) => {
    setDownloadingFiles(prev => new Set(prev).add(file.file_name));
    
    try {
      const filename = file.file_name;
      if (!filename || !cuit) return;
      
      // Check if it's a LufeCloudFile (no extension)
      if (!filename.includes('.')) {
        const response = await downloadLufeCloudFile(cuit, filename);
        if (response.success) {
          const fileBlob = new Blob([response.data], {
            type: 'application/pdf',
          });
          const url = window.URL.createObjectURL(fileBlob);
          window.open(url, '_blank');
        }
        setDownloadingFiles(prev => {
          const newSet = new Set(prev);
          newSet.delete(file.file_name);
          return newSet;
        });
        return;
      }
      
      // Determine which endpoint to use based on whether file comes from application_files
      const useApplicationEndpoint = file.is_application_file === true;
      
      let response;
      if (useApplicationEndpoint) {
        // For application files, use downloadSignerDocument (needs id_application)
        if (!file.id_application) {
          console.error('No application ID for application file');
          alert('No se puede descargar el archivo: falta el ID de la aplicación');
          return;
        }
        response = await downloadSignerDocument(file.id_application.toString(), cuit, filename);
      } else {
        // For client files, use downloadClientDocument
        response = await downloadClientDocument(cuit, filename);
      }
      
      if (response.success) {
        const mimeType = getMimeType(filename);
        const fileBlob = new Blob([response.data], { type: mimeType });
        const url = window.URL.createObjectURL(fileBlob);
        
        if (
          mimeType.startsWith('application/pdf') ||
          mimeType.startsWith('image/')
        ) {
          window.open(url, '_blank');
        } else {
          const a = document.createElement('a');
          a.href = url;
          a.download = filename;
          a.click();
          window.URL.revokeObjectURL(url);
        }
      } else {
        console.error('Error downloading file:', response);
        alert('Error al descargar el archivo: ' + (response.message || 'Error desconocido'));
      }
    } catch (error) {
      console.error('Error viewing file:', error);
      alert('Error al visualizar el archivo');
    } finally {
      setDownloadingFiles(prev => {
        const newSet = new Set(prev);
        newSet.delete(file.file_name);
        return newSet;
      });
    }
  };

  const handleDownloadFile = async (file: FileData) => {
    setDownloadingFiles(prev => new Set(prev).add(file.file_name));
    
    try {
      const filename = file.file_name;
      if (!filename || !cuit) return;
      
      // Check if it's a LufeCloudFile (no extension)
      if (!filename.includes('.')) {
        const response = await downloadLufeCloudFile(cuit, filename);
        if (response.success) {
          const fileBlob = new Blob([response.data], {
            type: 'application/pdf',
          });
          const url = window.URL.createObjectURL(fileBlob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `${filename}.pdf`;
          a.click();
          window.URL.revokeObjectURL(url);
        }
        setDownloadingFiles(prev => {
          const newSet = new Set(prev);
          newSet.delete(file.file_name);
          return newSet;
        });
        return;
      }
      
      // Determine which endpoint to use based on whether file comes from application_files
      const useApplicationEndpoint = file.is_application_file === true;
      
      let response;
      if (useApplicationEndpoint) {
        // For application files, use downloadSignerDocument (needs id_application)
        if (!file.id_application) {
          console.error('No application ID for application file');
          alert('No se puede descargar el archivo: falta el ID de la aplicación');
          return;
        }
        response = await downloadSignerDocument(file.id_application.toString(), cuit, filename);
      } else {
        // For client files, use downloadClientDocument
        response = await downloadClientDocument(cuit, filename);
      }
      
      if (response.success) {
        const mimeType = getMimeType(filename);
        const fileBlob = new Blob([response.data], { type: mimeType });
        const url = window.URL.createObjectURL(fileBlob);

        // Always force download for download button
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        a.click();
        window.URL.revokeObjectURL(url);
      } else {
        console.error('Error downloading file:', response);
        alert('Error al descargar el archivo: ' + (response.message || 'Error desconocido'));
      }
    } catch (error) {
      console.error('Error downloading file:', error);
      alert('Error al descargar el archivo');
    } finally {
      setDownloadingFiles(prev => {
        const newSet = new Set(prev);
        newSet.delete(file.file_name);
        return newSet;
      });
    }
  };

  // Helper function to download a file and return the blob (NO AUTO-DOWNLOAD)
  const downloadFileToBlob = async (file: FileData): Promise<{ blob: Blob; filename: string } | null> => {
    try {
      const filename = file.file_name;
      if (!filename) {
        console.warn('No filename provided for file:', file);
        return null;
      }

      let response;
      
      if (filename.includes('.')) {
        // Determine which endpoint to use based on whether file comes from application_files
        const useApplicationEndpoint = file.is_application_file === true;
        
        if (useApplicationEndpoint && file.id_application) {
          // For application files, use the application endpoint
          response = await downloadSignerDocument(file.id_application.toString(), cuit, filename);
        } else {
          // For client files, try client endpoint first, then fallback to cloud
          response = await downloadClientDocument(cuit, filename);
          
          if (!response.success && response.status === 500) {
            response = await downloadLufeCloudFile(cuit, filename.replace(/\.[^/.]+$/, ''));
          }
        }
        
        if (response && response.success && response.data) {
          const mimeType = getMimeType(filename);
          
          // Ensure we have valid blob data
          if (response.data instanceof Blob) {
            return {
              blob: response.data,
              filename: filename
            };
          } else {
            return {
              blob: new Blob([response.data], { type: mimeType }),
              filename: filename
            };
          }
        }
      } else {
        // Cloud document from LUFE
        response = await downloadLufeCloudFile(cuit, filename);
        if (response && response.success && response.data) {
          
          // Ensure we have valid blob data and preserve mime type
          if (response.data instanceof Blob) {
            // Use the actual blob type or default to pdf
            const fileExtension = response.data.type.startsWith('image/') 
              ? response.data.type.split('/')[1] 
              : 'pdf';
            return {
              blob: response.data,
              filename: `${filename}.${fileExtension}`
            };
          } else {
            return {
              blob: new Blob([response.data], { type: 'application/pdf' }),
              filename: `${filename}.pdf`
            };
          }
        }
      }
      
      console.warn(`Failed to fetch file data for: ${filename}`, response);
      return null;
    } catch (error) {
      console.error(`Error downloading file to blob (${file.file_name}):`, error);
      return null;
    }
  };

  const handleBulkDownload = async () => {
    if (selectedFiles.size === 0) {
      return;
    }
        
    setBulkDownloading(true);
    
    try {
      // Combine files from both sources and mark application files
      const allFiles = [
        ...files.map(f => ({ ...f, is_application_file: false })),
        ...application_files.map(f => ({ ...f, is_application_file: true }))
      ];
      const selectedFileObjects = allFiles.filter(file => selectedFiles.has(file.file_name));
      
      const zip = new JSZip();
      let successCount = 0;
      let failCount = 0;
      let authError = false;
      
      // Download all files and add them to the zip
      for (const file of selectedFileObjects) {
        try {
          const fileData = await downloadFileToBlob(file);
          
          if (fileData) {
            // Get document title for folder organization
            const documentTitle = getDocumentTitle(file.type);
            const folderName = documentTitle.replace(/[<>:"/\\|?*]/g, '_').replace(/\s+/g, '_');
            
            // Add file to appropriate folder in zip
            const folder = zip.folder(folderName);
            if (folder) {
              folder.file(fileData.filename, fileData.blob);
              successCount++;
            }
          } else {
            failCount++;
          }
        } catch (fileError: any) {
          failCount++;
          // Check if it's an authentication error
          if (fileError?.response?.status === 401 || fileError?.status === 401) {
            authError = true;
            break; // Stop trying if we get auth errors
          }
        }
        
        // Small delay between downloads to avoid overwhelming the server
        await new Promise(resolve => setTimeout(resolve, 300));
      }
      
      if (authError) {
        alert('Tu sesión ha expirado. Por favor, recarga la página e intenta nuevamente.');
        return;
      }
      
      if (successCount === 0) {
        alert(`No se pudo descargar ningún archivo. ${failCount} archivo(s) fallaron. Intenta descargarlos individualmente o recarga la página si el problema persiste.`);
        return;
      }
      
      if (failCount > 0) {
        const confirmed = window.confirm(`Se descargaron ${successCount} archivo(s) exitosamente, pero ${failCount} fallaron. ¿Deseas continuar con la descarga del ZIP con los archivos disponibles?`);
        if (!confirmed) {
          return;
        }
      }
      
      // Generate and download the ZIP file
      const zipBlob = await zip.generateAsync({ 
        type: 'blob',
        compression: 'DEFLATE',
        compressionOptions: { level: 6 }
      });
      
      // Create download link
      const url = window.URL.createObjectURL(zipBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `documentos_${cuit}_${new Date().toISOString().split('T')[0]}.zip`;
      link.style.display = 'none';
      
      // Force download
      document.body.appendChild(link);
      link.click();
      
      // Clean up
      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }, 100);
      
      
      
      // Clear selection after successful download
      setSelectedFiles(new Set());
      
    } catch (error) {
      alert('Error al crear el archivo ZIP. Por favor, inténtalo de nuevo.');
    } finally {
      setBulkDownloading(false);
    }
  };


  if ((!files || files.length === 0) && (!application_files || application_files.length === 0)) {
    return (
      <Card spacing="4" display="flex" justifyContent="center" alignItems="center" height="200px">
        <P type="1" weight="regular" modifier="weak">
          No hay documentos disponibles
        </P>
      </Card>
    );
  }

  return (
    <Card spacing="4">
      {/* Header with search and bulk actions */}
      <Card display="flex" justifyContent="space-between" alignItems="center" margin={{ bottom: '4' }}>
        <Card display="flex" alignItems="center" gap="12px" width="50%">
          <Input
            type='text'
            size='md'
            icons={{ iconLeft: 'search_outline' }}
            placeholder='Buscar por nombre'
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <Button variant="tertiary" size="xl">
            Buscar
          </Button> 
        </Card>
        
        <Card display="flex" gap="12px" alignItems="center">
          <P type="2" weight="regular" modifier="strong" variant="neutral">
            ({selectedFiles.size}) documentos seleccionados
          </P>
          <Button
            variant="tertiary"
            size="md"
            icon="download_outline"
            iconAlign="left"
            onClick={handleBulkDownload}
            disabled={bulkDownloading || selectedFiles.size === 0}
          >
            {bulkDownloading ? 'Creando ZIP...' : 'Descargar ZIP'}
          </Button>
        </Card>
      </Card>

      {Object.entries(groupedFiles)
        .sort(([typeA], [typeB]) => typeA.localeCompare(typeB))
        .map(([type, group]) => (
        <Card key={type} margin={{ bottom: '5' }}>
           <H type="4" weight="bold" modifier="strong" withMargin={false} variant="neutral" align="left">
             {group.title}
           </H>
          
          <Card margin={{ top: '3' }} display="flex" flexDirection="column" gap="8px">
            {group.files.map((file, index) => {
              const isSelected = selectedFiles.has(file.file_name);
              const isDownloading = downloadingFiles.has(file.file_name);
              const fileName = file.file_name?.includes('.') 
                ? file.file_name 
                : `${file.file_name}.pdf`;
              
              return (
                <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                  <div
                    key={`${file.file_name}-${index}`}
                    style={{
                      backgroundColor: isSelected ? theme.colors.success.bg.weak : theme.colors.neutral.bg.inverted,
                      borderColor: isSelected ? theme.colors.primary.border.regular : theme.colors.neutral.border.weak,
                      borderWidth: '1px',
                      borderStyle: 'solid',
                      borderRadius: '8px',
                      padding: '8px 12px',
                      width: '400px',
                      height: '40px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      boxSizing: 'border-box'
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1, minWidth: 0 }}>
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => handleFileSelection(file.file_name)}
                        style={{
                          width: '16px',
                          height: '16px',
                          accentColor: theme.colors.primary.bg.regular,
                          flexShrink: 0
                        }}
                      />
                      <span
                        style={{
                          fontSize: '14px',
                          fontWeight: '450',
                          color: theme.colors.neutral.text.regular,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          flex: 1
                        }}
                      >
                        {fileName}
                      </span>
                    </div>
                    
                      <div 
                        style={{ display: 'flex', gap: '4px', flexShrink: 0 }} 
                        className="documentation-tab-no-hover"
                        onClick={(e: React.MouseEvent) => e.stopPropagation()}
                      >
                        <Button
                          variant="tertiary"
                          size="sm"
                          icon="view_outline"
                          iconAlign="center"
                          onClick={() => handleViewFile(file)}
                          disabled={isDownloading}
                          aria-label="Ver archivo"
                        />
                        <Button
                          variant="tertiary"
                          size="sm"
                          icon="download_outline"
                          iconAlign="center"
                          onClick={() => handleDownloadFile(file)}
                          disabled={isDownloading}
                          aria-label="Descargar archivo"
                        />
                      </div>
                  </div>
                  
                  {file.updated_at && (
                    <span
                      style={{
                        fontSize: '12px',
                        fontWeight: '400',
                        color: theme.colors.neutral.text.weak,
                        flexShrink: 0,
                        whiteSpace: 'nowrap'
                      }}
                    >
                      Última actualización: {file.updated_at}
                    </span>
                  )}
                </div>
              );
            })}
          </Card>
        </Card>
      ))}
    </Card>
  );
};

export default DocumentationTab;
